import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { cardsApi } from '../api/cards';
import { referralsApi } from '../api/referrals';
import { authApi } from '../api/auth';
import { tournamentsApi } from '../api/tournaments';
import { usersApi } from '../api/users';
import { marketplaceApi } from '../api/marketplace';
import { supportApi } from '../api/support';
import { useWallet } from '../context/WalletContext';
import { useLanguage } from '../i18n/LanguageContext';
import { formatD10S } from '../utils/formatters';
import { sortGameweekTournamentsByRarity } from '../utils/gameweekTournamentOrder';
import { getTransactionDisplayAmount, getTransactionDisplayCurrency } from '../utils/transactionDisplay';
import { CreditCard, Settings, Users, Search, Filter, Eye, EyeOff, Trophy, Calendar, Clock, Wallet, ShoppingBag, Gavel, Repeat, ChevronLeft, ChevronRight, Info, X, ArrowLeftRight, Shield, History, AlertTriangle, Bell, Wrench, CheckCircle, AlertCircle } from 'lucide-react';
import '../styles/football-theme.css';
import '../styles/profile-gaming.css';
import GalleryTab from './profile/GalleryTab';
import OffersTab from './profile/OffersTab';
import ReferralsTab from './profile/ReferralsTab';
import LoginHistoryTab from './profile/LoginHistoryTab';
import SecurityTab from './profile/SecurityTab';
import NotificationsTab from './profile/NotificationsTab';
import SettingsTab from './profile/SettingsTab';
import ActivityReportTab from './profile/ActivityReportTab';
import CardsStatusTab from './profile/CardsStatusTab';

const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { connectWallet, disconnectWallet, account, isConnected } = useWallet();
  const { t, locale } = useLanguage();
  const [user, setUser] = useState(authApi.getCurrentUser());
  const [activeTab, setActiveTab] = useState('gallery'); // gallery, offers, settings, referrals, login-history, security
  const [loginHistory, setLoginHistory] = useState([]);
  const [loginHistoryTotal, setLoginHistoryTotal] = useState(0);
  const [securityReports, setSecurityReports] = useState([]);
  const [securityReportsTotal, setSecurityReportsTotal] = useState(0);
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportTicketsTotal, setSupportTicketsTotal] = useState(0);
  const [showSecurityReportModal, setShowSecurityReportModal] = useState(false);
  const [securityReportForm, setSecurityReportForm] = useState({
    reportType: 'other',
    title: '',
    description: '',
    priority: 'medium'
  });
  const [notifications, setNotifications] = useState([]);
  const [notificationsTotal, setNotificationsTotal] = useState(0);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [announcements, setAnnouncements] = useState([]);
  const [maintenanceAlerts, setMaintenanceAlerts] = useState([]);
  const [loadingCommunications, setLoadingCommunications] = useState(false);
  const [tradeOffers, setTradeOffers] = useState([]);
  const [counterOffers, setCounterOffers] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [cards, setCards] = useState([]);
  const [allCards, setAllCards] = useState([]);
  const [cardsForSale, setCardsForSale] = useState([]);
  const [soldCards, setSoldCards] = useState([]);
  const [listedLoans, setListedLoans] = useState([]);
  const [loanedCards, setLoanedCards] = useState([]);
  const [borrowedCards, setBorrowedCards] = useState([]);
  const [activityReport, setActivityReport] = useState(null);
  const [loadingCardsStatus, setLoadingCardsStatus] = useState(false);
  const [loadingActivityReport, setLoadingActivityReport] = useState(false);
  const [referralNetwork, setReferralNetwork] = useState(null);
  const [referralEarnings, setReferralEarnings] = useState([]);
  const [referralEarningsTotal, setReferralEarningsTotal] = useState(0);
  const [registeredTournaments, setRegisteredTournaments] = useState([]);
  const [allRegisteredTournaments, setAllRegisteredTournaments] = useState([]);
  const [pageRegisteredTournaments, setPageRegisteredTournaments] = useState(0);
  const [usedCardsInActiveGameweeks, setUsedCardsInActiveGameweeks] = useState(new Set());
  const [cardsInCoachTraining, setCardsInCoachTraining] = useState(new Set());
  const [openTradesMap, setOpenTradesMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [scoreCheckCountdowns, setScoreCheckCountdowns] = useState({}); // Map<tournamentId, minutesRemaining>
  /** Posizione/premio da GET /tournaments/:id/rankings (stessa fonte della pagina Ranking; final_score DB può essere stale). */
  const [profileGameweekPlacement, setProfileGameweekPlacement] = useState({});
  const [filters, setFilters] = useState({
    search: '',
    rarity: '',
    position: '',
    nationality: '',
    team: ''
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [inviteCopied, setInviteCopied] = useState(false);
  const tabsScrollRef = useRef(null);
  const [tabsScrollLeft, setTabsScrollLeft] = useState(false);
  const [tabsScrollRight, setTabsScrollRight] = useState(true);
  const [profileUserForRef, setProfileUserForRef] = useState(null); // linked user data per ref code quando admin
  const ITEMS_PER_PAGE = 10;
  const referralCodeValue = (user?.isAdmin && profileUserForRef)
    ? (profileUserForRef.referral_code || profileUserForRef.referralCode || '')
    : (user?.referral_code || user?.referralCode || '');
  const inviteLink = referralCodeValue
    ? `${window.location.origin}/register?ref=${encodeURIComponent(referralCodeValue)}`
    : '';
  // In profile, when admin use linked user (except for trades – trades always use user.id)
  const profileUserId = user?.isAdmin ? (user?.linkedUserId ?? user?.id) : user?.id;

  const handleCopyInviteLink = async () => {
    if (!inviteLink) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(inviteLink);
      } else {
        const tempInput = document.createElement('input');
        tempInput.value = inviteLink;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
      }
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy invite link:', error);
    }
  };

  const loadUserData = async () => {
    const currentUser = authApi.getCurrentUser();
    if (!currentUser?.id) return;
    if (currentUser?.isAdmin) {
      setUser(currentUser);
      return;
    }
    try {
      const response = await usersApi.getById(currentUser.id);
      if (response?.success && response?.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
        setUser(response.user);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const loadProfileUserForRef = async () => {
    if (!profileUserId || !user?.isAdmin) {
      setProfileUserForRef(null);
      return;
    }
    try {
      const response = await usersApi.getById(profileUserId);
      if (response?.success && response?.user) {
        setProfileUserForRef(response.user);
      } else {
        setProfileUserForRef(null);
      }
    } catch (error) {
      setProfileUserForRef(null);
    }
  };

  // Carica i dati aggiornati dell'utente quando il componente viene montato
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadProfileUserForRef();
  }, [profileUserId, user?.isAdmin]);

  // Apri un tab da link (es. /profile con state.openTab = 'notifications')
  useEffect(() => {
    const openTab = location.state?.openTab;
    if (openTab && ['gallery', 'offers', 'settings', 'referrals', 'login-history', 'security', 'notifications', 'activity', 'cards-status'].includes(openTab)) {
      setActiveTab(openTab);
      navigate(location.pathname, { replace: true, state: {} }); // rimuovi state per evitare loop
    }
  }, [location.state?.openTab]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    // Carica sempre i tornei a cui l'utente è iscritto
    loadRegisteredTournaments();
    
    if (activeTab === 'gallery') {
      setCurrentPage(0);
      loadCards();
      loadUsedCardsInActiveGameweeks();
      loadCardsInCoachTraining();
    } else if (activeTab === 'offers') {
      loadTradeOffers();
      loadCounterOffers();
      loadCounterOffers();
    } else if (activeTab === 'referrals') {
      loadReferralNetwork();
      loadReferralEarnings();
    } else if (activeTab === 'login-history') {
      loadLoginHistory();
    } else if (activeTab === 'security') {
      loadSecurityReports();
      loadSupportTickets();
    } else if (activeTab === 'notifications') {
      loadNotifications();
      loadAnnouncements();
      loadMaintenanceAlerts();
    } else if (activeTab === 'activity') {
      loadActivityReport();
    } else if (activeTab === 'cards-status') {
      loadCardsStatus();
    }
    if (activeTab === 'gallery') {
      loadUserCreatedOpenTrades();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filters, user]);

  useEffect(() => {
    if (activeTab === 'gallery' && allCards.length > 0) {
      const start = currentPage * ITEMS_PER_PAGE;
      setCards(allCards.slice(start, start + ITEMS_PER_PAGE));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, allCards]);

  const loadRegisteredTournaments = async () => {
    if (!profileUserId) return;
    
    try {
      const response = await tournamentsApi.getUserRegistered(profileUserId);
      if (response?.success) {
        // Raggruppa per gameweek (se sono gameweek)
        const tournaments = response.tournaments || [];
        
        // Raggruppa le gameweek
        const grouped = tournaments.reduce((acc, t) => {
          if (t.type === 'gameweek') {
            const nameParts = t.name.split(' - ');
            const gameweekName = nameParts.length >= 2 
              ? nameParts.slice(0, -1).join(' - ') 
              : nameParts[0];
            
            if (!acc[gameweekName]) {
              acc[gameweekName] = {
                name: gameweekName,
                type: 'gameweek',
                tournaments: [],
                startDate: t.start_date,
                endDate: t.end_date,
                status: t.status
              };
            }
            acc[gameweekName].tournaments.push(t);
          } else {
            // Altri tornei (non gameweek) li aggiungiamo direttamente
            acc[`single_${t.id}`] = t;
          }
          return acc;
        }, {});
        
        const allRegistered = Object.values(grouped).map((entry) => {
          if (entry.tournaments && entry.tournaments.length > 0) {
            return {
              ...entry,
              tournaments: sortGameweekTournamentsByRarity(entry.tournaments),
            };
          }
          return entry;
        });
        setAllRegisteredTournaments(allRegistered);
        const start = pageRegisteredTournaments * ITEMS_PER_PAGE;
        setRegisteredTournaments(allRegistered.slice(start, start + ITEMS_PER_PAGE));
      }
    } catch (error) {
      console.error('Error loading registered tournaments:', error);
      setRegisteredTournaments([]);
    }
  };

  // Aggiorna i tornei registrati ogni minuto
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      loadRegisteredTournaments();
    }, 60000); // Ogni minuto

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Calcola i countdown per i pulsanti "Check Scores"
  useEffect(() => {
    if (!profileUserId) return;

    const calculateCountdowns = () => {
      const countdowns = {};
      
      // Controlla tutti i tornei registrati (sia raggruppati che singoli)
      const allTournaments = [];
      registeredTournaments.forEach((item) => {
        if (item.tournaments && item.tournaments.length > 0) {
          // Gameweek raggruppate
          item.tournaments.forEach((tournament) => {
            if (tournament.status === 'in_progress') {
              allTournaments.push(tournament);
            }
          });
        } else if (item.status === 'in_progress') {
          // Tornei singoli
          allTournaments.push(item);
        }
      });
      
      // Log solo se ci sono tornei attivi (per evitare spam nella console)
      if (allTournaments.length > 0) {
        console.log(`[Countdown] Found ${allTournaments.length} in-progress tournaments:`, allTournaments.map(t => ({ id: t.id, name: t.name })));
      }
      
      allTournaments.forEach((tournament) => {
        // Usa prima localStorage (più aggiornato, viene settato immediatamente dopo il check)
        // Poi fallback a last_score_check dal database
        const lastCheckKey = `last_score_check_${tournament.id}_${profileUserId}`;
        let lastCheckTime = localStorage.getItem(lastCheckKey);
        
        // Se non c'è nel localStorage, usa quello dal database
        if (!lastCheckTime) {
          lastCheckTime = tournament.last_score_check;
        }
        
        if (lastCheckTime) {
          try {
            const lastCheck = new Date(lastCheckTime);
            const now = new Date();
            
            // Verifica che la data sia valida
            if (isNaN(lastCheck.getTime())) {
              console.warn(`Invalid date for tournament ${tournament.id}:`, lastCheckTime);
              countdowns[tournament.id] = { minutes: 0, seconds: 0 };
              return;
            }
            
            const minutesSinceLastCheck = (now.getTime() - lastCheck.getTime()) / (1000 * 60);
            
            if (minutesSinceLastCheck < 1) {
              const totalSecondsRemaining = 60 - ((now.getTime() - lastCheck.getTime()) / 1000); // 1 minute = 60 seconds
              const minutesRemaining = Math.floor(totalSecondsRemaining / 60);
              const secondsRemaining = Math.floor(totalSecondsRemaining % 60);
              
              // Log solo ogni 10 secondi per evitare spam (quando ci sono secondi rimanenti)
              if (secondsRemaining % 10 === 0 || secondsRemaining === 0) {
                console.log(`[Countdown] Tournament ${tournament.id}: ${minutesRemaining}m ${secondsRemaining}s remaining`);
              }
              
              countdowns[tournament.id] = {
                minutes: Math.max(0, minutesRemaining),
                seconds: Math.max(0, secondsRemaining)
              };
            } else {
              countdowns[tournament.id] = { minutes: 0, seconds: 0 };
            }
          } catch (error) {
            console.error(`Error calculating countdown for tournament ${tournament.id}:`, error);
            countdowns[tournament.id] = { minutes: 0, seconds: 0 };
          }
        } else {
          // Log solo una volta quando non c'è timestamp (non ogni secondo)
          // console.log(`[Countdown] Tournament ${tournament.id}: No timestamp found`);
          countdowns[tournament.id] = { minutes: 0, seconds: 0 };
        }
      });
      
      // Log solo se ci sono countdown attivi (per evitare spam)
      if (Object.keys(countdowns).length > 0 && Object.values(countdowns).some(c => c.minutes > 0 || c.seconds > 0)) {
        console.log(`[Countdown] Calculated countdowns:`, countdowns);
      }
      setScoreCheckCountdowns(countdowns);
    };

    // Calcola immediatamente
    calculateCountdowns();
    
    // Aggiorna il countdown ogni secondo per mostrare il tempo rimanente in tempo reale
    const interval = setInterval(() => {
      calculateCountdowns();
    }, 1000); // Ogni secondo

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, registeredTournaments]);

  useEffect(() => {
    if (allRegisteredTournaments.length > 0) {
      const start = pageRegisteredTournaments * ITEMS_PER_PAGE;
      setRegisteredTournaments(allRegisteredTournaments.slice(start, start + ITEMS_PER_PAGE));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageRegisteredTournaments]);

  useEffect(() => {
    setProfileGameweekPlacement({});
  }, [profileUserId]);

  const finishedGameweekTournamentIdsKey = useMemo(() => {
    const finishedIds = new Set();
    allRegisteredTournaments.forEach((item) => {
      if (!item.tournaments?.length) return;
      item.tournaments.forEach((t) => {
        if (String(t.type || '').toLowerCase() !== 'gameweek') return;
        const s = String(t.gameweek_status || t.status || '').toLowerCase();
        if (['pending_payment', 'closed', 'close'].includes(s)) {
          finishedIds.add(t.id);
        }
      });
    });
    return [...finishedIds].sort((a, b) => a - b).join(',');
  }, [allRegisteredTournaments]);

  useEffect(() => {
    if (!profileUserId || !finishedGameweekTournamentIdsKey) return;
    const ids = finishedGameweekTournamentIdsKey.split(',').map((x) => parseInt(x, 10)).filter((n) => !Number.isNaN(n));
    if (ids.length === 0) return;

    let cancelled = false;
    (async () => {
      const uid = Number(profileUserId);
      const next = {};
      await Promise.all(
        ids.map(async (tid) => {
          try {
            const res = await tournamentsApi.getRankings(tid, 2000);
            const rankings = Array.isArray(res?.rankings) ? res.rankings : [];
            const row = rankings.find((r) => Number(r.user_id) === uid);
            if (row) {
              next[tid] = {
                rank: row.rank != null ? Number(row.rank) : null,
                prize: row.prize != null ? Number(row.prize) : 0
              };
            }
          } catch {
            /* ignore single tournament */
          }
        })
      );
      if (!cancelled && Object.keys(next).length > 0) {
        setProfileGameweekPlacement((prev) => {
          const merged = { ...prev };
          Object.assign(merged, next);
          return merged;
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profileUserId, finishedGameweekTournamentIdsKey]);

  const loadCards = async () => {
    const cardsUserId = user?.isAdmin ? (user?.linkedUserId ?? user?.id) : user?.id;
    if (!cardsUserId) return;
    setLoading(true);
    try {
      const response = await cardsApi.getUserCards(cardsUserId, filters);
      if (response?.success) {
        const allCardsData = response.cards || [];
        const userId = typeof cardsUserId === 'string' ? parseInt(cardsUserId, 10) : cardsUserId;
        const ownedCards = allCardsData.filter((card) => {
          const ownerId = typeof card.owner_id === 'string' ? parseInt(card.owner_id, 10) : card.owner_id;
          const isBorrowed = card.acquired_via === 'loan';
          const isLoanedOut = !!card.is_on_loan;
          const isInSale = !!card.is_in_sale;
          const isInAuction = !!card.is_in_auction;
          // Borrowed cards: show regardless of ownership/loan flags (backend already guarantees they belong to this user)
          if (isBorrowed) return true;
          // Own cards: exclude if listed/auctioned/loaned out
          return ownerId === userId && !isLoanedOut && !isInSale && !isInAuction;
        });
        // Deduplica per evitare copie dopo cambio pagina (id + rarity = chiave unica)
        const seen = new Set();
        const uniqueOwnedCards = ownedCards.filter((card) => {
          const key = `${card.id ?? ''}-${card.rarity?.name ?? card.rarity_name ?? ''}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setAllCards(uniqueOwnedCards);
        const start = currentPage * ITEMS_PER_PAGE;
        setCards(uniqueOwnedCards.slice(start, start + ITEMS_PER_PAGE));
      } else if (response?.error) {
        console.error('Error loading cards:', response.error);
        setCards([]);
        setAllCards([]);
      }
    } catch (error) {
      console.error('Error loading cards:', error);
      setCards([]);
      setAllCards([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCardsStatus = async () => {
    const cardsUserId = user?.isAdmin ? (user?.linkedUserId ?? user?.id) : user?.id;
    if (!cardsUserId) return;
    setLoadingCardsStatus(true);
    try {
      const [listedResponse, soldResponse, listedLoansResponse, loanedResponse, borrowedResponse] = await Promise.all([
        marketplaceApi.getUserListedSales(cardsUserId, 50),
        marketplaceApi.getUserSoldSales(cardsUserId, 50),
        marketplaceApi.getUserListedLoans(cardsUserId, 50),
        marketplaceApi.getUserLoans(cardsUserId, 'lender', 50),
        marketplaceApi.getUserLoans(cardsUserId, 'borrower', 50)
      ]);

      setCardsForSale(listedResponse?.sales || []);
      setSoldCards(soldResponse?.sales || []);
      setListedLoans(listedLoansResponse?.loans || []);
      setLoanedCards(loanedResponse?.loans || []);
      setBorrowedCards(borrowedResponse?.loans || []);
    } catch (error) {
      console.error('Error loading cards status:', error);
      setCardsForSale([]);
      setSoldCards([]);
      setListedLoans([]);
      setLoanedCards([]);
      setBorrowedCards([]);
    } finally {
      setLoadingCardsStatus(false);
    }
  };

  const loadActivityReport = async () => {
    if (!profileUserId) return;
    setLoadingActivityReport(true);
    try {
      const response = await usersApi.getActivityReport(profileUserId, 7);
      if (response?.success) {
        setActivityReport(response);
      } else {
        setActivityReport(null);
      }
    } catch (error) {
      console.error('Error loading activity report:', error);
      setActivityReport(null);
    } finally {
      setLoadingActivityReport(false);
    }
  };

  const handleDownloadActivityCsv = () => {
    const entries = activityReport?.entries || [];
    if (entries.length === 0) return;

    const headers = ['Date', 'Category', 'Action', 'Amount', 'Currency', 'Status', 'Description'];
    const rows = entries.map((entry) => ([
      new Date(entry.createdAt).toISOString(),
      entry.category,
      entry.action,
      getTransactionDisplayAmount(entry),
      getTransactionDisplayCurrency(entry),
      entry.status,
      (entry.description || '').replace(/"/g, '""')
    ]));

    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell ?? ''}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'activity-report-7-days.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const loadUsedCardsInActiveGameweeks = async () => {
    if (!profileUserId) return;
    
    try {
      const response = await tournamentsApi.getUsedCardsInActiveGameweeks(profileUserId);
      // Backend sends `usedCards` (see GET .../used-cards-active-gameweeks); legacy alias `cardIds`
      const ids = response?.usedCards ?? response?.cardIds;
      if (response?.success && Array.isArray(ids)) {
        setUsedCardsInActiveGameweeks(new Set(ids.map((id) => Number(id)).filter((n) => Number.isFinite(n))));
      }
    } catch (error) {
      console.error('Error loading used cards in active gameweeks:', error);
    }
  };

  const loadCardsInCoachTraining = async () => {
    if (!profileUserId) return;
    try {
      const response = await cardsApi.getCoachTrainingActiveCards(profileUserId);
      if (response?.success && Array.isArray(response.cardIds)) {
        setCardsInCoachTraining(
          new Set(response.cardIds.map((id) => Number(id)).filter((n) => Number.isFinite(n)))
        );
      }
    } catch (error) {
      console.error('Error loading coach training cards:', error);
    }
  };

  const loadUserCreatedOpenTrades = async () => {
    if (!user?.id) return;
    try {
      const response = await marketplaceApi.getUserCreatedOpenTrades(user.id);
      if (response?.success && response?.trades) {
        setOpenTradesMap(response.trades);
      } else {
        setOpenTradesMap({});
      }
    } catch (error) {
      const isTradesDisabled = error?.message?.includes('Trades are currently disabled') || error?.status === 403;
      if (!isTradesDisabled) {
        console.error('Error loading user created open trades:', error);
      }
      setOpenTradesMap({});
    }
  };

  const loadReferralNetwork = async () => {
    const refsUserId = user?.isAdmin ? (user?.linkedUserId ?? user?.id) : user?.id;
    if (!refsUserId) return;
    setLoading(true);
    try {
      const response = await referralsApi.getNetwork(refsUserId);
      if (response?.success) {
        setReferralNetwork(response.network);
      } else if (response?.error) {
        console.error('Error loading referral network:', response.error);
        setReferralNetwork(null);
      }
    } catch (error) {
      console.error('Error loading referral network:', error);
      setReferralNetwork(null);
    } finally {
      setLoading(false);
    }
  };

  const loadReferralEarnings = async () => {
    const refsUserId = user?.isAdmin ? (user?.linkedUserId ?? user?.id) : user?.id;
    if (!refsUserId) return;
    setLoading(true);
    try {
      const response = await referralsApi.getEarnings(refsUserId, 200);
      if (response?.success) {
        setReferralEarnings(response.earnings || []);
        setReferralEarningsTotal(response.count || 0);
      }
    } catch (error) {
      console.error('Error loading referral earnings:', error);
      setReferralEarnings([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTradeOffers = async () => {
    if (!profileUserId) return;
    setLoadingOffers(true);
    try {
      const response = await marketplaceApi.getUserTrades(profileUserId, 100);
      if (response?.success && response?.trades) {
        const offers = response.trades.filter(trade => 
          trade.status === 'pending' && trade.user2Id === profileUserId
        );
        setTradeOffers(offers);
      }
    } catch (error) {
      console.error('Error loading trade offers:', error);
      setTradeOffers([]);
    } finally {
      setLoadingOffers(false);
    }
  };

  const loadCounterOffers = async () => {
    if (!user?.id) return;
    try {
      const response = await marketplaceApi.getCounterOffersForUser(user.id);
      if (response?.success && response?.offers) {
        setCounterOffers(response.offers);
      }
    } catch (error) {
      console.error('Error loading counter offers:', error);
      setCounterOffers([]);
    }
  };

  const loadLoginHistory = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const response = await usersApi.getLoginHistory(user.id, 50, 0);
      if (response?.success) {
        setLoginHistory(response.history || []);
        setLoginHistoryTotal(response.total || 0);
      }
    } catch (error) {
      console.error('Error loading login history:', error);
      setLoginHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSecurityReports = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const response = await usersApi.getSecurityReports(user.id, 50, 0);
      if (response?.success) {
        setSecurityReports(response.reports || []);
        setSecurityReportsTotal(response.total || 0);
      }
    } catch (error) {
      console.error('Error loading security reports:', error);
      setSecurityReports([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSupportTickets = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const response = await supportApi.getUserTickets(user.id, 50, 0);
      if (response?.success) {
        setSupportTickets(response.tickets || []);
        setSupportTicketsTotal(response.count || 0);
      }
    } catch (error) {
      console.error('Error loading support tickets:', error);
      setSupportTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const response = await usersApi.getNotifications(user.id, false, 50, 0);
      if (response?.success) {
        setNotifications(response.notifications || []);
        setNotificationsTotal(response.total || 0);
        const unread = (response.notifications || []).filter((n) => !n.isRead).length;
        setUnreadNotificationsCount(unread);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAnnouncements = async () => {
    setLoadingCommunications(true);
    try {
      const response = await usersApi.getAnnouncements();
      if (response?.success && response?.announcements) {
        setAnnouncements(response.announcements || []);
      }
    } catch (error) {
      console.error('Error loading announcements:', error);
      setAnnouncements([]);
    } finally {
      setLoadingCommunications(false);
    }
  };

  const loadMaintenanceAlerts = async () => {
    setLoadingCommunications(true);
    try {
      const response = await usersApi.getMaintenanceAlerts();
      console.log('Maintenance alerts response:', response);
      if (response?.success) {
        setMaintenanceAlerts(response.alerts || response.data?.alerts || []);
      } else {
        setMaintenanceAlerts([]);
      }
    } catch (error) {
      console.error('Error loading maintenance alerts:', error);
      setMaintenanceAlerts([]);
    } finally {
      setLoadingCommunications(false);
    }
  };

  const handleMarkNotificationAsRead = async (notificationId) => {
    if (!user?.id) return;
    
    try {
      await usersApi.markNotificationAsRead(user.id, notificationId);
      setNotifications(notifications.map((n) => 
        n.id === notificationId ? { ...n, isRead: true } : n
      ));
      setUnreadNotificationsCount(Math.max(0, unreadNotificationsCount - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllNotificationsAsRead = async () => {
    if (!user?.id) return;
    
    try {
      await usersApi.markAllNotificationsAsRead(user.id);
      setNotifications(notifications.map((n) => ({ ...n, isRead: true })));
      setUnreadNotificationsCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleCreateSecurityReport = async (e) => {
    e.preventDefault();
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const response = await usersApi.createSecurityReport({
        userId: user.id,
        ...securityReportForm
      });
      if (response?.success) {
        setShowSecurityReportModal(false);
        setSecurityReportForm({
          reportType: 'other',
          title: '',
          description: '',
          priority: 'medium'
        });
        loadSecurityReports();
      }
    } catch (error) {
      console.error('Error creating security report:', error);
      alert('Failed to create security report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'gallery', name: t('profile.tabs.myCards'), icon: CreditCard },
    { id: 'cards-status', name: t('profile.tabs.soldLoans'), icon: ShoppingBag },
    { id: 'activity', name: t('profile.tabs.activityReport'), icon: History },
    { id: 'offers', name: t('profile.tabs.offers'), icon: ArrowLeftRight, hidden: true },
    { id: 'referrals', name: t('profile.tabs.referrals'), icon: Users },
    { id: 'login-history', name: t('profile.tabs.loginHistory'), icon: History },
    { id: 'security', name: t('profile.tabs.security'), icon: Shield },
    { id: 'notifications', name: t('profile.tabs.notifications'), icon: Bell },
    { id: 'settings', name: t('profile.tabs.settings'), icon: Settings },
  ];

  const checkTabsOverflow = () => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const canScrollLeft = el.scrollLeft > 0;
    const canScrollRight = el.scrollLeft < el.scrollWidth - el.clientWidth - 2;
    setTabsScrollLeft(canScrollLeft);
    setTabsScrollRight(canScrollRight);
  };

  const scrollTabs = (direction) => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const amount = 200;
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
    setTimeout(checkTabsOverflow, 300);
  };

  useEffect(() => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const check = () => {
      const canScrollLeft = el.scrollLeft > 0;
      const canScrollRight = el.scrollLeft < el.scrollWidth - el.clientWidth - 2;
      setTabsScrollLeft(canScrollLeft);
      setTabsScrollRight(canScrollRight);
    };
    check();
    const resizeObserver = new ResizeObserver(check);
    resizeObserver.observe(el);
    el.addEventListener('scroll', check);
    return () => {
      resizeObserver.disconnect();
      el.removeEventListener('scroll', check);
    };
  }, []);

  return (
    <div className="profile-container min-h-screen pt-20 pb-8 px-4 sm:px-6 lg:px-8">
      {/* Profile Background */}
      <div 
        className="profile-background"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%), url('/images/homebg.png')`
        }}
      ></div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Registered Tournaments Banner - Mostra solo i tornei a cui l'utente è iscritto */}
        {registeredTournaments.length > 0 && (
          <div className="football-card glass-football p-6 mb-6 border-2 border-blue-500 shadow-lg shadow-blue-500/20 bg-gradient-to-r from-blue-500/10 to-blue-600/5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <Trophy className="w-6 h-6 text-blue-400" />
                <h2 className="text-xl font-bold text-blue-400">
                  {registeredTournaments.length} {t('tournaments.registered')}
                </h2>
              </div>
            </div>
            <div className="space-y-3">
              {registeredTournaments.map((item) => {
                // Se è una gameweek raggruppata
                if (item.tournaments && item.tournaments.length > 0) {
                  const startDate = new Date(item.startDate);
                  const endDate = new Date(item.endDate);
                  const now = new Date();
                  const isActive = item.status === 'in_progress' && now >= startDate && now <= endDate;
                  
                  return (
                    <div 
                      key={item.name} 
                      className="bg-gray-800/50 rounded-lg p-4 border border-blue-500/20 hover:border-blue-500/40 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">{item.name}</h3>
                          <div className="flex items-center space-x-4 text-sm text-gray-300 mb-2">
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>
                                {startDate.toLocaleDateString(locale || 'it-IT')} - {endDate.toLocaleDateString(locale || 'it-IT')}
                              </span>
                            </div>
                            {isActive && (
                              <div className="flex items-center space-x-1 text-green-400">
                                <Clock className="w-4 h-4" />
                                <span>{t('tournaments.statusInProgress')}</span>
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 mb-3">
                            {t('tournaments.rarityLabel')}: {item.tournaments.map(t => t.rarity_restriction || 'all').join(', ')}
                          </div>
                          {/* Show buttons for each tournament */}
                          <div className="flex flex-wrap gap-2">
                            {item.tournaments.map((tournament) => {
                              const status = tournament.gameweek_status || tournament.status;
                              const isInProgress = status === 'in_progress';
                              // pending_payment/closed = gameweek ended, show scores and ranking instead of Edit Formation
                              const isFinished = status === 'pending_payment' || status === 'closed' || status === 'close';
                              const showCheckScores = isInProgress || isFinished;
                              const placement = profileGameweekPlacement[tournament.id];
                              const rankDb =
                                tournament.final_rank != null
                                  ? Number(tournament.final_rank)
                                  : tournament.computed_rank != null
                                    ? Number(tournament.computed_rank)
                                    : tournament.computedRank != null
                                      ? Number(tournament.computedRank)
                                      : null;
                              const finalRank = placement?.rank ?? rankDb;
                              const prizeWon = tournament.prize_won != null ? Number(tournament.prize_won) : 0;
                              const prizeFromRankings = placement?.prize != null ? Number(placement.prize) : null;
                              const displayPrize =
                                prizeWon > 0 ? prizeWon : prizeFromRankings != null && prizeFromRankings > 0 ? prizeFromRankings : prizeWon;
                              const countdown = scoreCheckCountdowns[tournament.id] || { minutes: 0, seconds: 0 };
                              const minutesRemaining = countdown.minutes || 0;
                              const secondsRemaining = countdown.seconds || 0;
                              const isDisabled = isInProgress && (minutesRemaining > 0 || secondsRemaining > 0);
                              
                              // Debug log
                              if (isInProgress) {
                                console.log(`[Button] Tournament ${tournament.id}: countdown=${JSON.stringify(countdown)}, isDisabled=${isDisabled}`);
                              }
                              
                              // Formatta il tempo rimanente
                              const formatTime = () => {
                                if (minutesRemaining > 0 || secondsRemaining > 0) {
                                  if (minutesRemaining > 0) {
                                    return `${minutesRemaining}m ${secondsRemaining}s`;
                                  }
                                  return `${secondsRemaining}s`;
                                }
                                return '';
                              };
                              
                              return (
                                <div key={tournament.id} className="flex items-center gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (isDisabled) return; // Non navigare se disabilitato
                                      if (showCheckScores) {
                                        navigate(`/tournaments/${tournament.id}/scores`);
                                      } else {
                                        navigate(`/tournaments/${tournament.id}/formation`);
                                      }
                                    }}
                                    disabled={isDisabled}
                                    className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-all flex items-center gap-2 ${
                                      isDisabled
                                        ? 'bg-gray-500 text-gray-300 cursor-not-allowed opacity-60'
                                        : showCheckScores
                                          ? 'bg-green-500 hover:bg-green-600 text-white'
                                          : 'bg-yellow-500 hover:bg-yellow-600 text-black'
                                    }`}
                                    title={isDisabled ? `${t('common.processing')} ${formatTime()}` : ''}
                                  >
                                    {showCheckScores ? (
                                      <>
                                        <Clock className="w-4 h-4" />
                                        {isDisabled ? (
                                          <span>{t('common.processing')} {formatTime()} - {tournament.rarity_restriction || 'all'}</span>
                                        ) : (
                                          <span>{t('tournaments.checkScores')} - {tournament.rarity_restriction || 'all'}</span>
                                        )}
                                      </>
                                    ) : (
                                      <span>{t('tournaments.editFormation')} - {tournament.rarity_restriction || 'all'}</span>
                                    )}
                                  </button>
                                  {isFinished && (
                                    <span className="px-2 py-1 rounded-md text-xs font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/30">
                                      {t('rankings.tableRank')}: {finalRank != null && Number.isFinite(finalRank) ? finalRank : '-'} | {t('tournaments.prize')}: {`${Number(displayPrize || 0).toFixed(2)} DIOS`}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            isActive 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {isActive ? t('tournaments.statusInProgress') : t('tournaments.registered')}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                } else {
                  // Altri tornei (non gameweek)
                  const startDate = new Date(item.start_date);
                  const endDate = new Date(item.end_date);
                  const now = new Date();
                  const isActive = item.status === 'in_progress' && now >= startDate && now <= endDate;
                  
                  return (
                    <div 
                      key={item.id} 
                      className="bg-gray-800/50 rounded-lg p-4 border border-blue-500/20 hover:border-blue-500/40 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">{item.name}</h3>
                          <div className="flex items-center space-x-4 text-sm text-gray-300">
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>
                                {startDate.toLocaleDateString(locale || 'it-IT')} - {endDate.toLocaleDateString(locale || 'it-IT')}
                              </span>
                            </div>
                            {isActive && (
                              <div className="flex items-center space-x-1 text-green-400">
                                <Clock className="w-4 h-4" />
                                <span>{t('tournaments.statusInProgress')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {['in_progress', 'pending_payment', 'closed', 'close'].includes(item.status) ? (
                            (() => {
                              const countdown = scoreCheckCountdowns[item.id] || { minutes: 0, seconds: 0 };
                              const minutesRemaining = countdown.minutes || 0;
                              const secondsRemaining = countdown.seconds || 0;
                              const isDisabled = item.status === 'in_progress' && (minutesRemaining > 0 || secondsRemaining > 0);
                              
                              // Formatta il tempo rimanente
                              const formatTime = () => {
                                if (minutesRemaining > 0 || secondsRemaining > 0) {
                                  if (minutesRemaining > 0) {
                                    return `${minutesRemaining}m ${secondsRemaining}s`;
                                  }
                                  return `${secondsRemaining}s`;
                                }
                                return '';
                              };
                              
                              return (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isDisabled) return;
                                    navigate(`/tournaments/${item.id}/scores`);
                                  }}
                                  disabled={isDisabled}
                                  className={`px-4 py-2 font-semibold rounded-lg transition-all flex items-center gap-2 ${
                                    isDisabled
                                      ? 'bg-gray-500 text-gray-300 cursor-not-allowed opacity-60'
                                      : 'bg-green-500 hover:bg-green-600 text-white'
                                  }`}
                                  title={isDisabled ? `${t('common.processing')} ${formatTime()}` : ''}
                                >
                                  <Clock className="w-4 h-4" />
                                  {isDisabled ? `${t('common.processing')} ${formatTime()}` : t('tournaments.checkScores')}
                                </button>
                              );
                            })()
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/tournaments/${item.id}/formation`);
                              }}
                              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-lg transition-all"
                            >
                              {t('tournaments.editFormation')}
                            </button>
                          )}
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            isActive 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {isActive ? t('tournaments.statusInProgress') : t('tournaments.registered')}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          </div>
        )}
        
        {registeredTournaments.length === 0 && allRegisteredTournaments.length === 0 && (
          <div className="football-card glass-football p-6 mb-6 border-2 border-gray-500/30 bg-gray-500/5">
            <div className="text-center py-4">
              <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-400 mb-2">{t('tournaments.emptyTournaments')}</p>
              <button
                onClick={() => navigate('/tournaments')}
                className="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white"
              >
                {t('home.tournaments')}
              </button>
            </div>
          </div>
        )}

        {/* Pagination for Registered Tournaments */}
        {allRegisteredTournaments.length > ITEMS_PER_PAGE && (
          <div className="flex items-center justify-center space-x-4 mb-6">
            <button
              onClick={() => setPageRegisteredTournaments(Math.max(0, pageRegisteredTournaments - 1))}
              disabled={pageRegisteredTournaments === 0}
              className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                pageRegisteredTournaments === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              {t('common.previous')}
            </button>
            <span className="text-white/90 text-sm font-medium">
              {t('common.pageOf', { page: pageRegisteredTournaments + 1, total: Math.ceil(allRegisteredTournaments.length / ITEMS_PER_PAGE) })}
            </span>
            <button
              onClick={() => setPageRegisteredTournaments(Math.min(Math.ceil(allRegisteredTournaments.length / ITEMS_PER_PAGE) - 1, pageRegisteredTournaments + 1))}
              disabled={pageRegisteredTournaments >= Math.ceil(allRegisteredTournaments.length / ITEMS_PER_PAGE) - 1}
              className={`px-4 py-2 rounded-lg font-semibold transition-all flex items-center ${
                pageRegisteredTournaments >= Math.ceil(allRegisteredTournaments.length / ITEMS_PER_PAGE) - 1
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {t('common.next')}
              <ChevronRight className="w-5 h-5 ml-1" />
            </button>
          </div>
        )}

        {/* Profile Header */}
        <div className="football-card glass-football p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[#1e293b] mb-2">{user?.username || 'User'}</h1>
              <p className="text-[#475569]">{user?.email}</p>
              <div className="mt-4 flex flex-col gap-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div>
                    <span className="text-[#64748b] text-sm">Balance:</span>
                    <span className="text-[#4169E1] font-bold ml-2">{formatD10S(user?.d10s_balance || 0)} DIOS</span>
                  </div>
                  <div>
                    <span className="text-[#64748b] text-sm">Referral Code:</span>
                    <span className="text-[#4169E1] font-bold ml-2">{referralCodeValue || 'N/A'}</span>
                  </div>
                  {inviteLink && (
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="px-3 py-2 rounded-full border border-[rgba(65,105,225,0.35)] bg-[rgba(65,105,225,0.08)] backdrop-blur-sm">
                        <span className="text-[#64748b] text-xs uppercase tracking-wide mr-2">Invite</span>
                        <a
                          href={inviteLink}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#4169E1] font-semibold text-sm break-all"
                        >
                          {inviteLink}
                        </a>
                      </div>
                      <button
                        type="button"
                        onClick={handleCopyInviteLink}
                        className={`px-4 py-2 rounded-full font-semibold transition-all whitespace-nowrap border ${
                          inviteCopied
                            ? 'bg-green-500 text-white border-green-400'
                            : 'bg-[rgba(65,105,225,0.12)] text-[#1e293b] border-[rgba(65,105,225,0.35)] hover:bg-[rgba(65,105,225,0.2)]'
                        }`}
                      >
                        {inviteCopied ? 'Copied' : 'Copy link'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6">
          <button
            type="button"
            onClick={() => scrollTabs('left')}
            aria-label="Scroll tabs left"
            className={`profile-tabs-arrow flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-all shadow-lg ${
              tabsScrollLeft
                ? 'bg-white/95 text-[#4169E1] hover:bg-yellow-400 hover:text-black border-2 border-[#00D9FF] shadow-[0_0_12px_rgba(0,217,255,0.4)] hover:shadow-[0_0_16px_rgba(250,204,21,0.5)]'
                : 'bg-[rgba(0,0,0,0.3)] text-[#64748b] cursor-not-allowed opacity-50 border-2 border-[rgba(100,116,139,0.3)]'
            }`}
            disabled={!tabsScrollLeft}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div
            ref={tabsScrollRef}
            className="flex space-x-2 overflow-x-auto flex-1 min-w-0 profile-tabs-scroll"
          >
            {tabs.map((tab) => {
              if (tab.hidden) return null;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold whitespace-nowrap transition-all flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'bg-yellow-400 text-black'
                      : 'bg-white text-[#1e293b] hover:bg-[rgba(65,105,225,0.1)] border border-[rgba(65,105,225,0.2)]'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => scrollTabs('right')}
            aria-label="Scroll tabs right"
            className={`profile-tabs-arrow flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-all shadow-lg ${
              tabsScrollRight
                ? 'bg-white/95 text-[#4169E1] hover:bg-yellow-400 hover:text-black border-2 border-[#00D9FF] shadow-[0_0_12px_rgba(0,217,255,0.4)] hover:shadow-[0_0_16px_rgba(250,204,21,0.5)]'
                : 'bg-[rgba(0,0,0,0.3)] text-[#64748b] cursor-not-allowed opacity-50 border-2 border-[rgba(100,116,139,0.3)]'
            }`}
            disabled={!tabsScrollRight}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        {activeTab === 'gallery' && (
          <GalleryTab 
            cards={cards} 
            allCards={allCards}
            loading={loading} 
            filters={filters} 
            setFilters={setFilters}
            usedCardsInActiveGameweeks={usedCardsInActiveGameweeks}
            cardsInCoachTraining={cardsInCoachTraining}
            user={user}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            itemsPerPage={ITEMS_PER_PAGE}
            onCardListed={async () => {
              loadCards();
              loadUserData();
              await loadUserCreatedOpenTrades();
              loadCardsStatus();
              loadCardsInCoachTraining();
              loadUsedCardsInActiveGameweeks();
            }}
            openTradesMap={openTradesMap}
          />
        )}
        {activeTab === 'cards-status' && (
          <CardsStatusTab
            cardsForSale={cardsForSale}
            soldCards={soldCards}
            listedLoans={listedLoans}
            loanedCards={loanedCards}
            borrowedCards={borrowedCards}
            loading={loadingCardsStatus}
            user={user}
            onCardUnlisted={() => {
              loadCardsStatus();
              loadCards();
              loadUserCreatedOpenTrades();
            }}
          />
        )}
        {activeTab === 'activity' && (
          <ActivityReportTab
            report={activityReport}
            loading={loadingActivityReport}
            onDownloadCsv={handleDownloadActivityCsv}
          />
        )}
        {activeTab === 'offers' && (
          <OffersTab 
            offers={tradeOffers}
            counterOffers={counterOffers}
            loading={loadingOffers}
            user={user}
            onOfferUpdated={() => {
              loadTradeOffers();
              loadCounterOffers();
            }}
          />
        )}
        {activeTab === 'referrals' && (
          <ReferralsTab
            network={referralNetwork}
            earnings={referralEarnings}
            earningsTotal={referralEarningsTotal}
            loading={loading}
          />
        )}
        {activeTab === 'login-history' && (
          <LoginHistoryTab 
            history={loginHistory}
            total={loginHistoryTotal}
            loading={loading}
          />
        )}
        {activeTab === 'security' && (
          <SecurityTab 
            reports={securityReports}
            total={securityReportsTotal}
            supportTickets={supportTickets}
            supportTotal={supportTicketsTotal}
            loading={loading}
            showModal={showSecurityReportModal}
            setShowModal={setShowSecurityReportModal}
            form={securityReportForm}
            setForm={setSecurityReportForm}
            onSubmit={handleCreateSecurityReport}
            userId={user?.id}
            onSupportTicketsRefresh={loadSupportTickets}
          />
        )}
        {activeTab === 'notifications' && (
          <NotificationsTab 
            notifications={notifications}
            total={notificationsTotal}
            unreadCount={unreadNotificationsCount}
            announcements={announcements}
            maintenanceAlerts={maintenanceAlerts}
            loading={loading}
            loadingCommunications={loadingCommunications}
            onMarkAsRead={handleMarkNotificationAsRead}
            onMarkAllAsRead={handleMarkAllNotificationsAsRead}
          />
        )}
        {activeTab === 'settings' && (
          <SettingsTab 
            user={user} 
            setUser={setUser}
            connectWallet={connectWallet}
            disconnectWallet={disconnectWallet}
            account={account}
            isConnected={isConnected}
          />
        )}
      </div>
    </div>
  );
};

export default Profile;
